import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllUserNames } from "../../utils/account";
import { getSubscriptions } from "../../utils/billing";
import { getAllWorkspaceNames } from "../../utils/workspaces";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { buildUserNameMap, getDisplayName } from "../../utils/userNames";

const pad = (value: number) => value.toString().padStart(2, "0");

const formatDateTime = (value?: string) => {
  if (!value) return "-";

  const date = new Date(value);

  return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

const buildWorkspaceInfoMap = (
  workspaces: any[] = [],
  userNames: Record<string, string> = {},
) =>
  workspaces.reduce<Record<string, { name: string; owner: string }>>(
    (map, workspace) => {
      const id = workspace.id || workspace._id;
      if (id) {
        map[id] = {
          name: workspace.name || "-",
          owner: getDisplayName(workspace.addedBy, userNames),
        };
      }
      return map;
    },
    {},
  );

export default function Subscriptions() {
  const navigate = useNavigate();
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  const [workspaceInfo, setWorkspaceInfo] = useState<
    Record<string, { name: string; owner: string }>
  >({});
  const [loading, setLoading] = useState(false);
  const [showOnlyNonFree, setShowOnlyNonFree] = useState(true);

  useEffect(() => {
    const fetchSubscriptions = async () => {
      try {
        setLoading(true);
        const [response, usersResponse, workspacesResponse] = await Promise.all([
          getSubscriptions(showOnlyNonFree ? { isFreePlan: false } : {}),
          getAllUserNames().catch((error) => {
            console.error("Error fetching user names:", error);
            return { users: [] };
          }),
          getAllWorkspaceNames().catch((error) => {
            console.error("Error fetching workspace names:", error);
            return { workspaces: [] };
          }),
        ]);
        const userNameMap = buildUserNameMap(usersResponse.users || []);
        setUserNames(userNameMap);
        setWorkspaceInfo(
          buildWorkspaceInfoMap(workspacesResponse.workspaces || [], userNameMap),
        );
        setSubscriptions(response.data || []);
      } catch (error) {
        console.error("Error fetching subscriptions:", error);
        setSubscriptions([]);
        setUserNames({});
        setWorkspaceInfo({});
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptions();
  }, [showOnlyNonFree]);

  return (
    <div className="flex flex-col gap-4 p-6">
      <div>
        <h1 className="text-[24px] font-semibold text-[#211f26]">
          Subscriptions
        </h1>
        <p className="text-[13px] text-[#84828e]">
          Date format: dd-mm-yyyy hh:mm:ss
        </p>
      </div>

      <div className="rounded-2xl border border-[#eae7ec] bg-white p-5">
        <div className="mb-4 flex items-center gap-2">
          <label className="flex items-center gap-2 text-[14px] text-[#211f26]">
            <input
              type="checkbox"
              checked={showOnlyNonFree}
              onChange={(e) => setShowOnlyNonFree(e.target.checked)}
            />
            Fetch `isFreePlan=false`
          </label>
        </div>

        {loading ? (
          <div className="text-[14px] text-[#84828e]">Loading...</div>
        ) : (
          <>
            <Table className="w-full">
              <TableHeader>
                <TableRow className="border-b-0">
                  <TableHead className="bg-[#faf9fb]">S.No.</TableHead>
                  <TableHead className="bg-[#faf9fb]">Workspace ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Workspace Name</TableHead>
                  <TableHead className="bg-[#faf9fb]">Owner</TableHead>
                  <TableHead className="bg-[#faf9fb]">Status</TableHead>
                  <TableHead className="bg-[#faf9fb]">Customer ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Subscription ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Product ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Price ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Metadata Workspace ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Metadata Product ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Metadata Price ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Triggered By User ID</TableHead>
                  <TableHead className="bg-[#faf9fb]">Triggered By User Name</TableHead>
                  <TableHead className="bg-[#faf9fb]">Workflow</TableHead>
                  <TableHead className="bg-[#faf9fb]">Payment Mode</TableHead>
                  <TableHead className="bg-[#faf9fb]">Metadata Credits</TableHead>
                  <TableHead className="bg-[#faf9fb]">Negotiated Price</TableHead>
                  <TableHead className="bg-[#faf9fb]">Is Free Plan</TableHead>
                  <TableHead className="bg-[#faf9fb]">Current Period Start</TableHead>
                  <TableHead className="bg-[#faf9fb]">Current Period End</TableHead>
                  <TableHead className="bg-[#faf9fb]">Created At</TableHead>
                  <TableHead className="bg-[#faf9fb]">Updated At</TableHead>
                  <TableHead className="bg-[#faf9fb]">Canceled At</TableHead>
                  <TableHead className="bg-[#faf9fb]">Roll Over</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {subscriptions.map((item: any, index: number) => (
                  <TableRow key={item._id || item.subscriptionId || `${item.workspaceId}-${index}`}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.workspaceId || "-"}</TableCell>
                    <TableCell>
                      {item.workspaceId
                        ? workspaceInfo[item.workspaceId]?.name || "-"
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {item.workspaceId
                        ? workspaceInfo[item.workspaceId]?.owner || "-"
                        : "-"}
                    </TableCell>
                    <TableCell>{item.status || "-"}</TableCell>
                    <TableCell>{item.stripeCustomerId || item.customerId || "-"}</TableCell>
                    <TableCell>{item.stripeSubscriptionId || item.subscriptionId || "-"}</TableCell>
                    <TableCell>{item.stripeProductId || item.productId || "-"}</TableCell>
                    <TableCell>{item.stripePriceId || item.priceId || "-"}</TableCell>
                    <TableCell>{item.metadata?.workspaceId || "-"}</TableCell>
                    <TableCell>{item.metadata?.productId || "-"}</TableCell>
                    <TableCell>{item.metadata?.priceId || "-"}</TableCell>
                    <TableCell>{item.metadata?.triggeredByUserId || "-"}</TableCell>
                    <TableCell>
                      {item.metadata?.triggeredByUserId
                        ? userNames[item.metadata.triggeredByUserId] || "-"
                        : "-"}
                    </TableCell>
                    <TableCell>{item.metadata?.workflow || "-"}</TableCell>
                    <TableCell>{item.metadata?.paymentMode || "-"}</TableCell>
                    <TableCell>{item.metadata?.credits ?? "-"}</TableCell>
                    <TableCell>{item.metadata?.negotiatedPrice ?? "-"}</TableCell>
                    <TableCell>
                      {typeof item.metadata?.isFreePlan === "boolean"
                        ? String(item.metadata.isFreePlan)
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {formatDateTime(
                        item.currentPeriodStart || item.current_cycle_start
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDateTime(
                        item.currentPeriodEnd || item.current_cycle_end
                      )}
                    </TableCell>
                    <TableCell>{formatDateTime(item.createdAt)}</TableCell>
                    <TableCell>{formatDateTime(item.updatedAt)}</TableCell>
                    <TableCell>{formatDateTime(item.canceledAt)}</TableCell>
                    <TableCell>
                      <button
                        className="text-[#211f26] underline"
                        onClick={() => navigate(`/roll-over/${item.workspaceId}`)}
                      >
                        View Roll Over
                      </button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {subscriptions.length === 0 && (
              <div className="pt-4 text-[14px] text-[#84828e]">
                No subscriptions found.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
